"use client";

// import { useEffect, useRef } from "react";
import { TarotRequestCreate } from "@/types/tarot";
import { useI18n } from "@/i18n/I18nProvider";
// import { ErrorLogService, ErrorSeverity } from "@/services/errorLog";

interface Step7PreferencesProps {
  formData: TarotRequestCreate;
  situationContext: string;
  mainDoubts: string;
  fieldErrors: Set<string>;
  onSituationContextChange: (value: string) => void;
  onMainDoubtsChange: (value: string) => void;
  onFieldErrorRemove: (field: string) => void;
}

const STEP7_CONTEXT_MAX_LENGTH = 1200;
const STEP7_DOUBTS_MAX_LENGTH = 300;
type FieldEl = HTMLInputElement | HTMLTextAreaElement;

/* Legacy keyboard diagnostics paused in favor of the doubts popup events.
type ProbeState = {
  touchAt: number;
  vvHeightAtTouch: number;
  focusAt: number;
  vvHeightAtFocus: number;
  baselineHeight: number;
  typed: boolean;
  timer: number | null;
  suspectedAt: number;
  vvHeightAfter: number;
  switchedFromField: string | null;
};

type FieldEl = HTMLInputElement | HTMLTextAreaElement;

type ReportedWarning = {
  field: string;
  reportedAt: number;
  evidence: string;
};

const KEYBOARD_PROBE_DELAY_MS = 2500;
const KEYBOARD_HEIGHT_THRESHOLD_PX = 100;
const STEP7_CONTEXT_MAX_LENGTH = 1200;
const STEP7_DOUBTS_MAX_LENGTH = 300;

function viewportHeight(): number {
  if (typeof window === "undefined") return 0;
  return window.visualViewport?.height ?? window.innerHeight;
}

function isTouchDevice(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none)").matches
  );
}

function isEditableElement(element: Element | null): boolean {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    (element instanceof HTMLElement && element.isContentEditable)
  );
}

*/

export function Step7Preferences({
  formData,
  situationContext,
  mainDoubts,
  fieldErrors,
  onSituationContextChange,
  onMainDoubtsChange,
  onFieldErrorRemove,
}: Step7PreferencesProps) {
  const { t } = useI18n();
  void formData;

  // Instrumentação: mantém uma altura de repouso (sem campo focado) e só
  // classifica uma suspeita depois de uma janela de observação. Trocas diretas
  // entre campos são ignoradas porque o teclado já está aberto e a viewport não
  // precisa encolher novamente.
  /* Legacy keyboard warning/recovery logs and their probes are paused.
  const probeRef = useRef<Record<string, ProbeState>>({});
  const restingViewportHeightRef = useRef(0);
  const activeFieldRef = useRef<string | null>(null);
  const reportedWarningRef = useRef<ReportedWarning | null>(null);
  const recoveryReportedRef = useRef(false);

  useEffect(() => {
    const probes = probeRef.current;
    const captureRestingViewport = () => {
      if (isEditableElement(document.activeElement)) return;
      restingViewportHeightRef.current = Math.max(
        restingViewportHeightRef.current,
        viewportHeight(),
      );
    };

    captureRestingViewport();
    window.addEventListener("resize", captureRestingViewport);
    window.visualViewport?.addEventListener("resize", captureRestingViewport);

    return () => {
      window.removeEventListener("resize", captureRestingViewport);
      window.visualViewport?.removeEventListener(
        "resize",
        captureRestingViewport,
      );
      Object.values(probes).forEach((probe) => {
        if (probe.timer !== null) window.clearTimeout(probe.timer);
      });
    };
  }, []);

  const getProbe = (field: string): ProbeState => {
    if (!probeRef.current[field]) {
      probeRef.current[field] = {
        touchAt: 0,
        vvHeightAtTouch: 0,
        focusAt: 0,
        vvHeightAtFocus: 0,
        baselineHeight: 0,
        typed: false,
        timer: null,
        suspectedAt: 0,
        vvHeightAfter: 0,
        switchedFromField: null,
      };
    }
    return probeRef.current[field];
  };

  const reportWarning = (
    field: string,
    evidence: string,
    data: Record<string, unknown>,
    reportedAt: number,
  ) => {
    if (reportedWarningRef.current) return; // um alerta por montagem do step
    reportedWarningRef.current = { field, reportedAt, evidence };
    ErrorLogService.logError(
      new Error("[Step7 keyboard] keyboard_did_not_open"),
      {
        component: "Step7Preferences",
        severity: ErrorSeverity.INFO,
        extra_data: {
          type: "step7_keyboard_probe",
          field,
          reason: "keyboard_did_not_open",
          evidence,
          observation_window_ms: KEYBOARD_PROBE_DELAY_MS,
          ...data,
        },
      },
    ).catch(() => {});
  };

  const reportRecovery = (field: string, recoveredAt: number) => {
    const warning = reportedWarningRef.current;
    if (!warning || recoveryReportedRef.current) return;
    recoveryReportedRef.current = true;
    ErrorLogService.logError(
      new Error("[Step7 keyboard] keyboard_recovered_after_warning"),
      {
        component: "Step7Preferences",
        severity: ErrorSeverity.INFO,
        extra_data: {
          type: "step7_keyboard_probe_recovery",
          field,
          reason: "keyboard_recovered_after_warning",
          warning_field: warning.field,
          warning_evidence: warning.evidence,
          recovery_after_ms: recoveredAt - warning.reportedAt,
        },
      },
    ).catch(() => {});
  };

  const handleTouchStart = (field: string) => () => {
    const probe = getProbe(field);
    const previousField = activeFieldRef.current;

    if (previousField && previousField !== field) {
      const previousProbe = getProbe(previousField);
      if (previousProbe.timer !== null) {
        window.clearTimeout(previousProbe.timer);
        previousProbe.timer = null;
      }
      previousProbe.suspectedAt = 0;
    }

    if (
      previousField === field &&
      probe.suspectedAt > 0 &&
      !probe.typed
    ) {
      reportWarning(field, "repeated_touch_without_input", {
        used_input: false,
        focus_duration_ms: Date.now() - probe.focusAt,
        vv_baseline: Math.round(probe.baselineHeight),
        vv_height_after: Math.round(probe.vvHeightAfter),
        resting_vv_height: Math.round(restingViewportHeightRef.current),
        still_focused: true,
      }, Date.now());
    }

    probe.touchAt = Date.now();
    probe.vvHeightAtTouch = viewportHeight();
    probe.switchedFromField =
      previousField && previousField !== field ? previousField : null;
  };

  const handleProbeFocus =
    (field: string) => (event: React.FocusEvent<FieldEl>) => {
      if (!isTouchDevice()) return;
      const probe = getProbe(field);
      const target = event.currentTarget;
      activeFieldRef.current = field;
      probe.focusAt = Date.now();
      probe.typed = false;
      probe.suspectedAt = 0;
      probe.vvHeightAfter = 0;
      probe.vvHeightAtFocus = viewportHeight();
      const touchWasRecent =
        probe.touchAt > 0 && probe.focusAt - probe.touchAt < 2000;
      if (!touchWasRecent) probe.switchedFromField = null;
      probe.baselineHeight = Math.max(
        restingViewportHeightRef.current,
        touchWasRecent ? probe.vvHeightAtTouch : 0,
        probe.vvHeightAtFocus,
      );
      if (probe.timer !== null) window.clearTimeout(probe.timer);

      // Ao tocar no segundo campo, o teclado pode continuar aberto. Nesse caso
      // não existe uma nova redução de viewport a ser observada.
      if (probe.switchedFromField) {
        probe.timer = null;
        return;
      }

      probe.timer = window.setTimeout(() => {
        const after = viewportHeight();
        probe.timer = null;
        probe.vvHeightAfter = after;
        const keyboardOpened =
          probe.baselineHeight - after > KEYBOARD_HEIGHT_THRESHOLD_PX;
        if (
          !probe.typed &&
          !keyboardOpened &&
          document.activeElement === target
        ) {
          // Ainda não reporta: aguarda abandono (blur) ou um segundo toque no
          // mesmo campo. Isso separa falha real de um usuário apenas lendo.
          probe.suspectedAt = Date.now();
        }
      }, KEYBOARD_PROBE_DELAY_MS);
    };

  const handleProbeInput = (field: string) => () => {
    const probe = getProbe(field);
    probe.typed = true;
    probe.suspectedAt = 0;
    if (probe.timer !== null) {
      window.clearTimeout(probe.timer);
      probe.timer = null;
    }
    reportRecovery(field, Date.now());
  };

  const handleProbeBlur = (field: string) => () => {
    const probe = getProbe(field);
    const focusDuration = probe.focusAt ? Date.now() - probe.focusAt : 0;
    if (probe.timer !== null) {
      window.clearTimeout(probe.timer);
      probe.timer = null;
    }
    if (probe.suspectedAt > 0 && !probe.typed) {
      reportWarning(field, "blur_after_probe_without_input", {
        used_input: false,
        focus_duration_ms: focusDuration,
        touch_to_focus_ms: probe.touchAt
          ? probe.focusAt - probe.touchAt
          : null,
        vv_height_at_touch: Math.round(probe.vvHeightAtTouch),
        vv_height_at_focus: Math.round(probe.vvHeightAtFocus),
        vv_baseline: Math.round(probe.baselineHeight),
        vv_height_after: Math.round(probe.vvHeightAfter),
        resting_vv_height: Math.round(restingViewportHeightRef.current),
        window_inner_height: window.innerHeight,
        still_focused: false,
        has_visual_viewport: !!window.visualViewport,
      }, Date.now());
    }
    probe.suspectedAt = 0;
    if (activeFieldRef.current === field) activeFieldRef.current = null;
  };

  */

  // Props compartilhadas entre <input> e <textarea> para o mesmo campo.
  // extraClass permite adicionar `resize-none` só ao <textarea>.
  const fieldProps = (
    field: string,
    value: string,
    onChange: (value: string) => void,
    placeholder: string,
    extraClass = "",
  ) => {
    const maxLength =
      field === "step7_context"
        ? STEP7_CONTEXT_MAX_LENGTH
        : STEP7_DOUBTS_MAX_LENGTH;

    return {
      id: field,
      name: field,
      value,
      maxLength,
      onChange: (e: React.ChangeEvent<FieldEl>) => {
        onChange(e.target.value.slice(0, maxLength));
        if (fieldErrors.has(field)) onFieldErrorRemove(field);
      },
      className: `form-input-compact w-full focus:border-[rgba(232,115,115,0.5)] focus:outline-none transition-all duration-300 ${
        fieldErrors.has(field) ? "border-red-500" : "border-[rgba(232,115,115,0.3)]"
      }${extraClass ? ` ${extraClass}` : ""}`,
      style: { scrollMarginBottom: "50vh" },
      autoComplete: "off" as const,
      // onTouchStart: handleTouchStart(field),
      // onFocus: handleProbeFocus(field),
      // onInput: handleProbeInput(field),
      // onBlur: handleProbeBlur(field),
      placeholder,
      "aria-invalid": fieldErrors.has(field),
    };
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/50 rounded-lg p-3 border border-[rgba(232,115,115,0.2)] mb-3">
        <div className="flex items-center space-x-2">
          <div className="text-xl">✍️</div>
          <p className="form-description-text">
            {t(
              "step7.header.desc.prefix",
              "Preencha o breve contexto da situação e ",
            )}
            <strong>
              {t(
                "step7.header.desc.highlight",
                "suas 3 principais dúvidas.",
              )}
            </strong>
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="step7_context" className="form-label-compact">
          {t("step7.context.label", "Breve contexto da situação")}{" "}
          <span className="text-red-600">*</span>
        </label>
        <textarea
          rows={5}
          {...fieldProps(
            "step7_context",
            situationContext,
            onSituationContextChange,
            t(
              "step7.context.placeholder",
              "Estamos sem nos falar há 2 semanas após um desentendimento.\nDesde então, ele ficou mais distante e eu não sei como interpretar esse silêncio.",
            ),
            "resize-none",
          )}
        />
        {fieldErrors.has("step7_context") && (
          <p className="text-red-600 text-xs mt-1">
            {t(
              "step7.context.error",
              "Escreva um breve contexto da situação para continuar.",
            )}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="step7_main_doubts" className="form-label-compact">
          {t("step7.doubts.label", "Suas 3 principais dúvidas")}{" "}
          <span className="text-red-600">*</span>
        </label>
        <textarea
          rows={4}
          {...fieldProps(
            "step7_main_doubts",
            mainDoubts,
            onMainDoubtsChange,
            t(
              "step7.doubts.placeholder",
              "Ele ainda pensa em mim?\nExiste chance de reaproximação?\nO que ele sente e ainda não demonstra?",
            ),
            "resize-none",
          )}
        />
        <p className="text-right text-xs text-gray-500 mt-1" aria-live="polite">
          {mainDoubts.length}/{STEP7_DOUBTS_MAX_LENGTH}
        </p>
        {fieldErrors.has("step7_main_doubts") && (
          <p className="text-red-600 text-xs mt-1">
            {t(
              "step7.doubts.error",
              "Informe suas 3 principais dúvidas para continuar.",
            )}
          </p>
        )}
      </div>

      <div className="bg-white/40 rounded-lg p-3 border border-[rgba(232,115,115,0.15)]">
        <p className="form-description-text">
          📝{" "}
          <span className="font-title">
            {t("step7.tip.title", "Última etapa:")}
          </span>{" "}
          {t(
            "step7.tip.text",
            "Falta apenas um passo para finalizar sua leitura.",
          )}
        </p>
      </div>

      {/* Keep scroll space stable before, during and between touches. Removing
          it on blur can clamp the page scroll before the next field gets focus.
          svh also keeps this space stable when mobile browser chrome changes. */}
      <div
        aria-hidden="true"
        className="h-[45vh] supports-[height:1svh]:h-[45svh] [@media(hover:hover)]:hidden"
      />
    </div>
  );
}
